import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputNumberModule } from 'primeng/inputnumber';
import { MultiSelectModule } from 'primeng/multiselect';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { AinFormFieldComponent } from '../../shared/design-system/form-field.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { JalaliDateInputComponent } from '../../shared/design-system/jalali-date-input.component';
import { GrowthSegmentSelectorComponent } from '../../shared/design-system/growth-segment-selector.component';
import { OfferingSelectorComponent } from '../../shared/design-system/offering-selector.component';
import { GrowthAgentService } from './growth-agent.service';
import { GrowthDistributionService, GrowthStrategyActivationPlan } from './growth-distribution.service';
import { isStrategyArtifactApproved, strategyArtifactTypeI18nKey } from './growth-status.util';
import {
  buildGrowthContentPillarOptions,
  buildGrowthPublishChannelOptions,
  growthPublishChannelLabel
} from './growth-channel.constants';

interface ActivationPlanFormModel {
  strategyArtifactId: string;
  planningHorizonDays: number;
  primarySegmentId: string | null;
  primaryOfferingId: string | null;
  channelKeys: string[];
  contentPillars: string[];
}

@Component({
  standalone: true,
  selector: 'ain-growth-strategy-activation-plan-detail',
  imports: [
    FormsModule,
    AinPageComponent,
    AinStateComponent,
    AinFormFieldComponent,
    InfoHintComponent,
    JalaliDateInputComponent,
    GrowthSegmentSelectorComponent,
    OfferingSelectorComponent,
    RouterLink,
    TableModule,
    ButtonModule,
    SelectModule,
    MultiSelectModule,
    InputNumberModule,
    AinDatePipe
  ],
  template: `
    <ain-page
      [eyebrow]="t('growth.eyebrow')"
      [title]="title()"
      [description]="t('growth.strategy.activation.description')">
      <div class="toolbar">
        <a pButton routerLink="/growth/strategy/activation-plans" severity="secondary" [label]="t('common.back')"></a>
      </div>

      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else {
        <section class="card">
          <div class="section-title">
            <h3>{{ t('growth.strategy.activation.dialog.createTitle') }}</h3>
            <ain-info-hint
              [titleKey]="'growth.strategy.activation.dialog.help.title'"
              [descriptionKey]="'growth.strategy.activation.dialog.help.description'" />
          </div>

          @if (plan(); as row) {
            <p class="meta">
              {{ t('growth.strategy.columns.status') }}:
              <strong>{{ activationStatusLabel(row.status) }}</strong>
              · {{ t('growth.strategy.activation.columns.integrationStatus') }}:
              <strong>{{ integrationStatusLabel(row) }}</strong>
            </p>
          }

          <div class="dialog-grid">
            <ain-form-field [label]="t('growth.strategy.activation.fields.strategyArtifact')">
              <p-select
                [options]="artifactOptions()"
                optionLabel="label"
                optionValue="value"
                [(ngModel)]="model.strategyArtifactId"
                appendTo="body"
                [disabled]="!isNew()"
                filter />
              @if (artifactOptions().length === 0) {
                <a routerLink="/growth/agents/strategy-artifacts" pButton severity="help" size="small" [label]="t('growth.strategy.activation.actions.openStrategyArtifacts')"></a>
              }
            </ain-form-field>
            <ain-form-field [label]="t('growth.strategy.activation.fields.horizonDays')">
              <p-inputNumber [(ngModel)]="model.planningHorizonDays" [min]="7" [max]="180" [disabled]="!isNew()" />
            </ain-form-field>
            <ain-form-field [label]="t('growth.strategy.activation.fields.segment')">
              <ain-growth-segment-selector [(ngModel)]="model.primarySegmentId" />
            </ain-form-field>
            <ain-form-field [label]="t('growth.strategy.activation.fields.offering')">
              <ain-offering-selector [(ngModel)]="model.primaryOfferingId" />
            </ain-form-field>
            <ain-form-field [label]="t('growth.strategy.activation.fields.channels')">
              <p-multiSelect
                [options]="channelOptions()"
                optionLabel="label"
                optionValue="value"
                [(ngModel)]="model.channelKeys"
                appendTo="body"
                [disabled]="!isNew()"
                display="chip" />
            </ain-form-field>
            <ain-form-field [label]="t('growth.strategy.activation.fields.pillars')">
              <p-multiSelect
                [options]="pillarOptions()"
                optionLabel="label"
                optionValue="value"
                [(ngModel)]="model.contentPillars"
                appendTo="body"
                [disabled]="!isNew()"
                display="chip" />
            </ain-form-field>
          </div>

          <div class="actions">
            @if (isNew()) {
              <button pButton type="button" [label]="t('common.save')" [disabled]="!canSavePlan()" (click)="savePlan()"></button>
            } @else if (plan(); as row) {
              @if (isActivationAwaitingApproval(row.status)) {
                <button pButton type="button" [label]="t('growth.strategy.actions.submit')" (click)="requestApproval(row.id)"></button>
              }
              @if (isActivationPendingApproval(row)) {
                @if (hasIntentDeepLink(row)) {
                  <a [routerLink]="['/workflow/intents', row.workflowIntentRequestId]" pButton severity="contrast" [label]="t('growth.strategy.activation.actions.openIntentDetail')"></a>
                } @else {
                  <a routerLink="/workflow/intents/approvals/inbox" pButton severity="secondary" [label]="t('growth.strategy.activation.actions.openApprovalInbox')"></a>
                }
              }
              @if (isActivationDeferred(row)) {
                <a routerLink="/workflow/intents/governance" pButton severity="warn" [label]="t('growth.strategy.activation.actions.openGovernance')"></a>
              }
              @if (isActivationApproved(row.status)) {
                <button pButton type="button" severity="help" [label]="t('growth.strategy.activation.actions.generateCalendar')" (click)="previewCalendar()"></button>
              }
            }
          </div>
        </section>

        @if (!isNew() && calendarPreviewItems().length) {
          <section class="card">
            <h3>{{ t('growth.strategy.activation.calendar.dialogTitle') }}</h3>
            <ain-form-field class="calendar-start-field" [label]="t('growth.strategy.activation.calendar.startDate')">
              <ain-date-input [(ngModel)]="calendarStartUtc" />
            </ain-form-field>
            <ain-form-field class="calendar-start-field" [label]="t('growth.marketing.calendar.cadenceDays')">
              <p-inputNumber [(ngModel)]="calendarCadenceDays" [min]="1" [showButtons]="true" />
            </ain-form-field>
            <p-table [value]="calendarPreviewItems()" [rows]="8" [paginator]="true">
              <ng-template pTemplate="header">
                <tr>
                  <th>{{ t('growth.marketing.calendar.columns.channel') }}</th>
                  <th>{{ t('growth.strategy.activation.columns.objective') }}</th>
                  <th>{{ t('growth.marketing.calendar.columns.scheduledAtUtc') }}</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td>{{ channelLabel(row.channelKey) }}</td>
                  <td>{{ row.title || row.sourceObjectiveKey }}</td>
                  <td>{{ row.scheduledAtUtc | ainDate:'datetime' }}</td>
                </tr>
              </ng-template>
            </p-table>
            <div class="actions">
              <button pButton type="button" [label]="t('growth.strategy.activation.calendar.preview')" (click)="previewCalendar()"></button>
              <button pButton type="button" severity="help" [disabled]="!calendarPreviewToken" [label]="t('growth.strategy.activation.calendar.commit')" (click)="commitCalendar()"></button>
            </div>
          </section>
        }
      }
    </ain-page>
  `,
  styles: [`
    .toolbar { margin-bottom: 12px; }
    .card { border: 1px solid #e5e7eb; border-radius: 12px; padding: 12px; margin-bottom: 12px; }
    .section-title { display: flex; align-items: center; gap: 8px; }
    .meta { margin: 8px 0 12px; color: #4b5563; }
    .dialog-grid { display: grid; gap: 14px; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); margin-top: 12px; }
    .actions { display: flex; gap: 8px; flex-wrap: wrap; margin-top: 12px; }
    .calendar-start-field { max-width: 320px; }
  `]
})
export class GrowthStrategyActivationPlanDetailComponent implements OnInit {
  private readonly distribution = inject(GrowthDistributionService);
  private readonly agent = inject(GrowthAgentService);
  private readonly i18n = inject(LocalizationService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  readonly loading = signal(true);
  readonly isNew = signal(true);
  readonly plan = signal<GrowthStrategyActivationPlan | null>(null);
  readonly artifactOptions = signal<Array<{ label: string; value: string }>>([]);
  readonly deferredCodes = signal<Record<string, string>>({});
  readonly calendarPreviewItems = signal<Array<{ channelKey: string; title?: string; sourceObjectiveKey?: string | null; scheduledAtUtc: string }>>([]);

  model: ActivationPlanFormModel = this.emptyPlanModel();
  calendarStartUtc = new Date().toISOString().slice(0, 10);
  calendarCadenceDays = 1;
  calendarPreviewToken = '';

  ngOnInit(): void {
    this.loadArtifacts();
    const id = this.route.snapshot.paramMap.get('activationPlanId');
    this.isNew.set(!id || id === 'new');
    const artifactId = this.route.snapshot.queryParamMap.get('artifactId');
    if (artifactId) {
      this.model = { ...this.model, strategyArtifactId: artifactId };
    }

    if (this.isNew()) {
      this.loading.set(false);
      return;
    }

    this.distribution.getActivationPlan(id!).subscribe({
      next: row => {
        this.plan.set(row);
        this.model = {
          strategyArtifactId: row.strategyArtifactId,
          planningHorizonDays: row.planningHorizonDays,
          primarySegmentId: this.parseStringArray(row.segmentRefsJson)[0] ?? null,
          primaryOfferingId: this.parseStringArray(row.offeringRefsJson)[0] ?? null,
          channelKeys: this.parseChannelKeys(row.channelMixJson),
          contentPillars: this.parseStringArray(row.contentPillarsJson)
        };
        this.loading.set(false);
      },
      error: () => this.loading.set(false)
    });
  }

  title(): string {
    return this.isNew()
      ? this.t('growth.strategy.activation.actions.newPlan')
      : this.t('growth.strategy.activation.dialog.createTitle');
  }

  loadArtifacts(): void {
    this.agent.listStrategyArtifacts().subscribe(items => {
      const approved = items.filter(a => isStrategyArtifactApproved(a.status));
      this.artifactOptions.set(approved.map(a => ({
        label: `${a.title} (${this.t(strategyArtifactTypeI18nKey(a.artifactType))})`,
        value: a.id
      })));
    });
  }

  channelOptions(): Array<{ label: string; value: string }> {
    return buildGrowthPublishChannelOptions(key => this.t(key));
  }

  pillarOptions(): Array<{ label: string; value: string }> {
    return buildGrowthContentPillarOptions(key => this.t(key));
  }

  canSavePlan(): boolean {
    return Boolean(this.model.strategyArtifactId && this.model.channelKeys.length > 0 && this.model.contentPillars.length > 0);
  }

  savePlan(): void {
    if (!this.canSavePlan()) return;
    const pillars = this.model.contentPillars;
    this.distribution.createActivationPlan({
      strategyArtifactId: this.model.strategyArtifactId,
      planningHorizonDays: this.model.planningHorizonDays,
      objectiveJson: JSON.stringify(pillars.map(p => ({ key: p, label: this.pillarLabel(p) }))),
      kpiTargetsJson: '[]',
      segmentRefsJson: JSON.stringify(this.model.primarySegmentId ? [this.model.primarySegmentId] : []),
      offeringRefsJson: JSON.stringify(this.model.primaryOfferingId ? [this.model.primaryOfferingId] : []),
      channelMixJson: JSON.stringify(this.model.channelKeys),
      contentPillarsJson: JSON.stringify(pillars),
      riskSnapshotJson: '{}',
      idempotencyKey: `activation-${Date.now()}`,
      correlationId: crypto.randomUUID()
    }).subscribe({
      next: saved => {
        this.router.navigate(['/growth/strategy/activation-plans', saved.id]);
      }
    });
  }

  requestApproval(id: string): void {
    this.distribution.requestActivationPlanApproval(id).subscribe({
      next: updated => {
        this.clearDeferred(id);
        this.plan.set(updated);
      },
      error: error => {
        this.markDeferred(id, this.extractErrorCode(error));
      }
    });
  }

  previewCalendar(): void {
    const planId = this.plan()?.id;
    if (!planId) return;
    this.distribution.previewCalendar({
      activationPlanId: planId,
      startUtc: new Date(this.calendarStartUtc).toISOString(),
      cadenceDays: this.calendarCadenceDays,
      idempotencyKey: `preview-${Date.now()}`
    }).subscribe(res => {
      this.calendarPreviewToken = res.previewToken;
      this.calendarPreviewItems.set(res.items);
    });
  }

  commitCalendar(): void {
    const planId = this.plan()?.id;
    if (!planId || !this.calendarPreviewToken) return;
    this.distribution.commitCalendar({
      activationPlanId: planId,
      previewToken: this.calendarPreviewToken,
      cadenceDays: this.calendarCadenceDays,
      idempotencyKey: `commit-${Date.now()}`
    }).subscribe({
      next: () => {
        this.calendarPreviewToken = '';
        this.calendarPreviewItems.set([]);
      }
    });
  }

  isActivationApproved(status: string | number): boolean {
    return status === 'Approved' || status === 2;
  }

  isActivationAwaitingApproval(status: string | number): boolean {
    return status === 'Draft' || status === 0 || status === 'PendingApproval' || status === 1;
  }

  isActivationPendingApproval(row: GrowthStrategyActivationPlan): boolean {
    const pending = row.status === 'PendingApproval' || Number(row.status) === 1;
    return pending && !!row.workflowProcessInstanceId && !this.isActivationDeferred(row);
  }

  isActivationDeferred(row: GrowthStrategyActivationPlan): boolean {
    return this.isActivationAwaitingApproval(row.status) &&
      Boolean(row.lastWorkflowDeferredCode || this.deferredCodes()[row.id]);
  }

  hasIntentDeepLink(row: GrowthStrategyActivationPlan): boolean {
    return typeof row.workflowIntentRequestId === 'string' && row.workflowIntentRequestId.length > 0;
  }

  integrationStatusLabel(row: GrowthStrategyActivationPlan): string {
    if (this.isActivationDeferred(row)) return this.t('growth.strategy.activation.integration.deferred');
    if (this.isActivationPendingApproval(row)) return this.t('growth.strategy.activation.integration.pendingApproval');
    if (this.isActivationApproved(row.status)) return this.t('growth.strategy.activation.integration.executed');
    return this.t('growth.strategy.activation.integration.planned');
  }

  activationStatusLabel(status: string | number): string {
    if (status === 0 || status === 'Draft') return this.t('growth.strategy.activation.status.draft');
    if (status === 1 || status === 'PendingApproval') return this.t('growth.strategy.activation.status.pending');
    if (status === 2 || status === 'Approved') return this.t('growth.strategy.activation.status.approved');
    if (status === 3 || status === 'Rejected') return this.t('growth.strategy.activation.status.rejected');
    return String(status);
  }

  channelLabel(channelKey: string): string {
    return growthPublishChannelLabel(this.normalizeChannelKey(channelKey), key => this.t(key));
  }

  pillarLabel(key: string): string {
    const labelKey = `growth.strategy.pillars.${key}`;
    const translated = this.t(labelKey);
    return translated === labelKey ? key : translated;
  }

  t(key: string): string {
    return this.i18n.translate(key);
  }

  private normalizeChannelKey(channelKey: string): string {
    const prefix = 'growth.publish.channels.';
    return channelKey.startsWith(prefix) ? channelKey.slice(prefix.length) : channelKey;
  }

  private emptyPlanModel(): ActivationPlanFormModel {
    return {
      strategyArtifactId: '',
      planningHorizonDays: 30,
      primarySegmentId: null,
      primaryOfferingId: null,
      channelKeys: ['web-blog', 'web-landing', 'social-organic'],
      contentPillars: ['awareness', 'consideration', 'conversion']
    };
  }

  private parseStringArray(json: string): string[] {
    try {
      const parsed = JSON.parse(json) as unknown;
      if (!Array.isArray(parsed)) return [];
      return parsed.filter(item => typeof item === 'string') as string[];
    } catch {
      return [];
    }
  }

  private parseChannelKeys(channelMixJson: string): string[] {
    try {
      const parsed = JSON.parse(channelMixJson) as unknown;
      if (!Array.isArray(parsed)) return [];
      return parsed
        .map(item => {
          if (typeof item === 'string') return item;
          if (item && typeof item === 'object' && 'channelKey' in item) {
            const key = (item as { channelKey?: string }).channelKey;
            return typeof key === 'string' ? key : null;
          }
          return null;
        })
        .filter((item): item is string => Boolean(item));
    } catch {
      return [];
    }
  }

  private markDeferred(planId: string, code: string): void {
    this.deferredCodes.set({
      ...this.deferredCodes(),
      [planId]: code
    });
  }

  private clearDeferred(planId: string): void {
    const current = { ...this.deferredCodes() };
    delete current[planId];
    this.deferredCodes.set(current);
  }

  private extractErrorCode(error: unknown): string {
    const fallback = this.t('growth.strategy.activation.integration.deferredDefaultCode');
    if (!error || typeof error !== 'object') return fallback;

    const response = error as {
      error?: {
        error?: string;
        Error?: string;
        code?: string;
        reasonCode?: string;
      } | string;
    };

    if (typeof response.error === 'string' && response.error.trim().length > 0) {
      return response.error.trim();
    }

    if (response.error && typeof response.error === 'object') {
      const payload = response.error;
      const code = payload.error ?? payload.Error ?? payload.code ?? payload.reasonCode;
      if (typeof code === 'string' && code.trim().length > 0) {
        return code.trim();
      }
    }

    return fallback;
  }
}
